import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { getChatCompletion } from '../../../services/aiIntegrations/chatCompletion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import toast from 'react-hot-toast';

const PLATFORM_COLORS_MAP = {
  facebook: '#1877F2',
  instagram: '#E1306C',
  google: '#EA4335',
  all: '#7C3AED',
};

const AdAnalyticsTab = ({ campaigns }) => {
  const [aiSummary, setAiSummary] = useState('');
  const [loadingSummary, setLoadingSummary] = useState(false);

  const totalImpressions = campaigns?.reduce((s, c) => s + (c?.impressions || 0), 0);
  const totalClicks = campaigns?.reduce((s, c) => s + (c?.clicks || 0), 0);
  const totalLeads = campaigns?.reduce((s, c) => s + (c?.leads || 0), 0);
  const totalSpend = campaigns?.reduce((s, c) => s + (parseFloat(c?.spend) || 0), 0);
  const avgCTR = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100)?.toFixed(2) : '0.00';
  const avgCPL = totalLeads > 0 ? (totalSpend / totalLeads)?.toFixed(2) : '0.00';

  // Platform breakdown
  const platformData = ['facebook', 'instagram', 'google', 'all']?.map((p) => {
    const pCampaigns = campaigns?.filter(c => c?.platform === p);
    return {
      platform: p?.charAt(0)?.toUpperCase() + p?.slice(1),
      impressions: pCampaigns?.reduce((s, c) => s + (c?.impressions || 0), 0),
      clicks: pCampaigns?.reduce((s, c) => s + (c?.clicks || 0), 0),
      leads: pCampaigns?.reduce((s, c) => s + (c?.leads || 0), 0),
      spend: pCampaigns?.reduce((s, c) => s + (parseFloat(c?.spend) || 0), 0),
      fill: PLATFORM_COLORS_MAP?.[p],
    };
  })?.filter(p => p?.impressions > 0 || p?.spend > 0);

  // Campaign performance chart data
  const campaignChartData = campaigns
    ?.filter(c => c?.impressions > 0 || c?.clicks > 0)
    ?.slice(0, 8)
    ?.map(c => ({
      name: c?.name?.length > 15 ? c?.name?.slice(0, 15) + '…' : c?.name,
      impressions: c?.impressions || 0,
      clicks: c?.clicks || 0,
      leads: c?.leads || 0,
      spend: parseFloat(c?.spend) || 0,
    }));

  // Best performing campaigns
  const bestCampaigns = [...campaigns]
    ?.filter(c => c?.leads > 0)
    ?.sort((a, b) => (b?.leads || 0) - (a?.leads || 0))
    ?.slice(0, 3);

  // CPL by platform
  const cplByPlatform = platformData?.map(p => ({
    platform: p?.platform,
    cpl: p?.leads > 0 ? (p?.spend / p?.leads)?.toFixed(2) : null,
    leads: p?.leads,
    spend: p?.spend,
  }))?.filter(p => p?.cpl !== null);

  const handleGetWeeklySummary = async () => {
    if (campaigns?.length === 0) { toast?.error('No campaign data to analyze'); return; }
    setLoadingSummary(true);
    setAiSummary('');
    try {
      const campaignSummary = campaigns?.slice(0, 10)?.map(c => ({
        name: c?.name,
        platform: c?.platform,
        status: c?.status,
        impressions: c?.impressions,
        clicks: c?.clicks,
        leads: c?.leads,
        spend: c?.spend,
        budget: c?.daily_budget,
      }));

      const response = await getChatCompletion(
        'ANTHROPIC',
        'claude-sonnet-4-5-20250929',
        [{
          role: 'user',
          content: `Write a concise weekly marketing performance summary (3-4 sentences) for these campaigns:
Total: ${totalImpressions?.toLocaleString()} impressions, ${totalClicks?.toLocaleString()} clicks, ${totalLeads} leads, $${totalSpend?.toFixed(2)} spent, ${avgCTR}% CTR, $${avgCPL} CPL
Campaigns: ${JSON.stringify(campaignSummary)}
Include key wins, areas to improve, and one actionable recommendation.`
        }],
        { max_tokens: 300, temperature: 0.6 }
      );
      setAiSummary(response?.choices?.[0]?.message?.content || '');
    } catch (err) {
      toast?.error('Failed to generate summary');
    } finally {
      setLoadingSummary(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* KPI Strip */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        {[
          { label: 'Impressions', value: totalImpressions?.toLocaleString(), icon: 'Eye', color: 'text-blue-600' },
          { label: 'Clicks', value: totalClicks?.toLocaleString(), icon: 'MousePointerClick', color: 'text-green-600' },
          { label: 'CTR', value: `${avgCTR}%`, icon: 'TrendingUp', color: 'text-violet-600' },
          { label: 'Leads', value: totalLeads, icon: 'Users', color: 'text-orange-600' },
          { label: 'Total Spend', value: `$${totalSpend?.toFixed(2)}`, icon: 'DollarSign', color: 'text-red-600' },
          { label: 'Avg CPL', value: `$${avgCPL}`, icon: 'Target', color: 'text-teal-600' },
        ]?.map((kpi, i) => (
          <div key={i} className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-1.5 mb-1">
              <Icon name={kpi?.icon} size={14} className={kpi?.color} />
              <span className="text-xs text-muted-foreground">{kpi?.label}</span>
            </div>
            <p className="text-lg font-bold text-foreground">{kpi?.value}</p>
          </div>
        ))}
      </div>
      {/* Campaign Performance Chart */}
      {campaignChartData?.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4">Campaign Performance</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={campaignChartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="impressions" fill="#7C3AED" radius={[3, 3, 0, 0]} />
              <Bar dataKey="clicks" fill="#22C55E" radius={[3, 3, 0, 0]} />
              <Bar dataKey="leads" fill="#F59E0B" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Best Performing Ads */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Trophy" size={15} className="text-amber-500" />
            Best Performing Campaigns
          </h3>
          {bestCampaigns?.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No lead data yet</p>
          ) : (
            <div className="space-y-3">
              {bestCampaigns?.map((c, i) => (
                <div key={c?.id} className="flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-slate-100 text-slate-600' : 'bg-orange-50 text-orange-600'
                  }`}>{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{c?.name}</p>
                    <p className="text-xs text-muted-foreground">{c?.platform} · {c?.leads} leads</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-foreground">${c?.spend > 0 && c?.leads > 0 ? (c?.spend / c?.leads)?.toFixed(2) : '—'}</p>
                    <p className="text-xs text-muted-foreground">CPL</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* CPL by Platform */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="BarChart2" size={15} className="text-violet-500" />
            Cost Per Lead by Platform
          </h3>
          {cplByPlatform?.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No CPL data yet</p>
          ) : (
            <div className="space-y-3">
              {cplByPlatform?.map((p, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground w-20 flex-shrink-0">{p?.platform}</span>
                  <div className="flex-1 bg-muted rounded-full h-2">
                    <div
                      className="h-2 rounded-full bg-violet-500"
                      style={{ width: `${Math.min(100, (parseFloat(p?.cpl) / 100) * 100)}%` }}
                    />
                  </div>
                  <span className="text-sm font-bold text-foreground w-16 text-right">${p?.cpl}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* AI Weekly Summary */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Icon name="Sparkles" size={15} className="text-violet-500" />
            AI Weekly Summary
          </h3>
          <button
            onClick={handleGetWeeklySummary}
            disabled={loadingSummary}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 text-white rounded-lg text-xs font-medium hover:bg-violet-700 disabled:opacity-50 transition-colors"
          >
            {loadingSummary ? (
              <div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Icon name="RefreshCw" size={12} />
            )}
            Generate Report
          </button>
        </div>
        {aiSummary ? (
          <p className="text-sm text-foreground leading-relaxed">{aiSummary}</p>
        ) : (
          <p className="text-sm text-muted-foreground">Click "Generate Report" to get an AI-powered weekly performance summary.</p>
        )}
      </div>
    </div>
  );
};

export default AdAnalyticsTab;
